<template>
  <FormCaption
    :subtitle="t('schedule.notifications.subtitle')"
    :title="t('schedule.notifications.title')"
  >
    <div class="snm-Management">
      <div
        v-for="channel in channelsWithWebHooksFields"
        :key="channel[NOTIFICATION_CHANNEL_ENTITY_KEYS.VALUE]"
        class="snm-Management_Item"
      >
        <div class="snm-ItemHeader">
          <div class="snm-ItemHeader_Icon">
            <AppIcon
              :icon-name="channel[NOTIFICATION_CHANNEL_ENTITY_KEYS.ICON]"
              height="24"
              width="24"
            />
          </div>

          <div class="snm-ItemHeader_Label">
            {{ channel[NOTIFICATION_CHANNEL_ENTITY_KEYS.LABEL] }}
          </div>

          <AppToggle
            v-model="selectedChannelsModel"
            :data-auto-testid="NOTIFICATION_CHANNEL_TOGGLE_TEST_ID"
            :data-testid="NOTIFICATION_CHANNEL_TOGGLE_TEST_ID"
            :disabled="Boolean(channel[NOTIFICATION_CHANNEL_ENTITY_KEYS.DISABLED])"
            :value="channel[NOTIFICATION_CHANNEL_ENTITY_KEYS.VALUE]"
            name="channel-picker"
          />
        </div>

        <div v-if="channel.webHooksFields" class="snm-ItemBody">
          <ScheduleWebhooksSelect
            v-model="
              channelsAttributesModel[channel.webHooksFields.channelTypeId][
                channel.webHooksFields.attributeTypeId
              ]
            "
            :data-auto-testid="NOTIFICATION_WEBHOOK_FIELD_WRAPPER_TEST_ID"
            :data-testid="NOTIFICATION_WEBHOOK_FIELD_WRAPPER_TEST_ID"
            :loading="webHooksLoaders[channel.webHooksFields.uid] || false"
            :options="webHooksOptions[channel.webHooksFields.uid] || []"
            @request-options="
              getWebHooks({
                uid: channel.webHooksFields.uid,
                attributeTypeId: channel.webHooksFields.attributeTypeId,
                channelTypeId: channel.webHooksFields.channelTypeId
              })
            "
          />
        </div>
      </div>
    </div>
  </FormCaption>
</template>

<script setup>
import { isEmpty } from 'lodash'
import { computed, reactive } from 'vue'
import { useI18n } from 'vue-i18n'

import IntegrationApiHandler from '@/api/integrations'
import { getResolvedNotificationsChannels } from '@/utils/check-ins/schedules-helpers'
import { NOTIFICATION_CHANNEL_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { NOTIFICATIONS_CHANNELS_BY_TYPE_ID } from '@/utils/okr-elements/notifications'

import FormCaption from '@/components/check-ins/FormCaption'
import {
  NOTIFICATION_CHANNEL_TOGGLE_TEST_ID,
  NOTIFICATION_WEBHOOK_FIELD_WRAPPER_TEST_ID
} from '@/components/check-ins/jest-helpers'
import ScheduleWebhooksSelect from '@/components/check-ins/ScheduleWebhooksSelect'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import AppToggle from '@/components/ui/AppToggle/AppToggle'

defineOptions({
  name: 'ScheduleNotificationsManagement'
})

const props = defineProps({
  excludedChannels: {
    type: Array,
    default: () => []
  }
})

const resolvedChannels = computed(() =>
  getResolvedNotificationsChannels(() => props.excludedChannels)
)

const { t } = useI18n()

const selectedChannelsModel = defineModel('selectedChannels', {
  type: Array,
  required: true,
  set: value => value
})

const channelsAttributesModel = defineModel('channelsAttributes', {
  type: Object,
  required: true,
  set: value => value
})

const webHooksOptions = reactive({})
const webHooksLoaders = reactive({})
const api = new IntegrationApiHandler()

const getWebHooks = async ({ uid, channelTypeId, attributeTypeId } = {}) => {
  if (!uid || !channelTypeId || !attributeTypeId) {
    throw new Error('getWebHooks: uid, channelTypeId and attributeTypeId are required')
  }

  try {
    webHooksLoaders[uid] = true

    const data = await api.getChannelsAttributes({
      attributeTypeId,
      channelTypeId
    })

    webHooksOptions[uid] = data.map(item => {
      const { id, attributeValue } = item
      return {
        id: id,
        label: attributeValue.name,
        value: id
      }
    })
  } catch (error) {
    handleError({ error })
  } finally {
    webHooksLoaders[uid] = false
  }
}

const webHooksFields = computed(() => {
  const NOTIFICATIONS_CHANNELS_TYPE_IDS = Object.values(resolvedChannels.value).map(
    ch => ch[NOTIFICATION_CHANNEL_ENTITY_KEYS.VALUE]
  )
  // that's need to render fields for selected channels in same order as they are rendered as checkboxes
  const selectedChannels = NOTIFICATIONS_CHANNELS_TYPE_IDS.filter(channelTypeId =>
    selectedChannelsModel.value.includes(channelTypeId)
  )
  return selectedChannels.reduce((acc, channelTypeId) => {
    const channelAttributes = channelsAttributesModel.value[channelTypeId]

    if (isEmpty(channelAttributes)) {
      return acc
    }

    const allAttributes = Object.keys(channelAttributes)
      .filter(attributeId => {
        const attributeOptions =
          NOTIFICATIONS_CHANNELS_BY_TYPE_ID[channelTypeId].attributes[attributeId] || {}

        return attributeOptions.isWebHook
      })
      .map(attributeTypeId => {
        return {
          channelTypeId,
          attributeTypeId: Number(attributeTypeId),
          uid: NOTIFICATIONS_CHANNELS_BY_TYPE_ID[channelTypeId].attributes[attributeTypeId].uid
        }
      })

    return [...acc, ...allAttributes]
  }, [])
})

const channelsWithWebHooksFields = computed(() => {
  return Object.fromEntries(
    Object.entries(resolvedChannels.value).map(([key, channel]) => {
      const channelTypeId = Number(channel[NOTIFICATION_CHANNEL_ENTITY_KEYS.VALUE])

      const webHooksFieldsForChannel = webHooksFields.value.find(
        field => field.channelTypeId === channelTypeId
      )

      if (webHooksFieldsForChannel) {
        return [key, { ...channel, webHooksFields: webHooksFieldsForChannel }]
      }

      return [key, channel]
    })
  )
})
</script>

<style lang="scss" scoped>
.snm-Management {
  display: flex;
  flex-direction: column;
  gap: 12px;
  --fc-caption-content-mt: 8px;
  --fc-caption-subtitle-mt: 0;
  --at-title-level-6-fs: #{$fs-12};
  --at-title-level-6-lh: 18px;
  --as-trigger-button-border-radius: #{$border-radius-md};
  --as-trigger-button-append-icon-mr: 8px;
}

.snm-Management_Item {
  border: 1px solid var(--gray-blue-100);
  border-radius: $border-radius-md;
  display: flex;
  flex-direction: column;
  --snm-header-height: 72px;
  background: linear-gradient(
    180deg,
    $white var(--snm-header-height),
    var(--gray-0) var(--snm-header-height)
  );
}

.snm-ItemHeader {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: var(--snm-header-height);
}

.snm-ItemHeader_Icon {
  height: 32px;
  width: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: $border-radius-sm-next;
  background-color: var(--gray-blue-0);
  color: $dark-2;
}

.snm-ItemHeader_Label {
  color: var(--gray-blue-700);
  font-size: #{$fs-14};
  font-weight: #{fw('semi-bold')};
  margin-right: auto;
}

.snm-ItemHeader,
.snm-ItemBody {
  padding-inline: 15px;
  width: 100%;
}

.snm-ItemBody {
  border-top: 1px solid var(--gray-blue-100);
  padding-block: 8px 15px;
}
</style>
